var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { BapUrlParam, INCLUDE_FEES_URL_PARAM, INCLUDE_TAXES_URL_PARAM, LOYALTY_OFFER_KEY, LOYALTY_URL_PARAM, OFFER_KEYS_URL_PARAM, } from '../../utils/constants';
import { CalculatorFields, CalculatorUrlKeys, PaymentFrequency, PaymentMethod, loyaltyOfferChanged, primaryCalculatorId, setPrimaryCalculatorOverriddenIsSellingProvince, urlParsedOnInitialLoad as updatePrimaryCalculatorFromUrl, } from '../../core/reducers/financialCalculators';
import { urlParsedOnInitialLoad as updateCurrentVehicleFromUrl, } from '../../core/reducers/currentVehicle';
import { setWarrantyOptionsModalType } from '../../core/reducers/ui';
import { brandNameDetected, defaultPaymentMethodOptionsDetected, defaultPaymentOptionsDetected, } from '../../core/reducers/brandConfig';
import { canSkipDefaultNonStackableOffersChanged, canUseLoyaltyChanged, selectCanUseLoyalty, } from '../../core/reducers/offerOptions';
import { getDefaultPaymentMethodConfig, getDefaultPaymentOptionsConfig } from '../../utils/constants/payment';
import { loyaltyPrimaryChanged, offersPrimaryChanged } from '../../core/reducers/offers';
import { setIsLoyaltyFilterChecked } from '../../core/reducers/specialOffers';
import { useAppName } from '../../utils/sitecoreContext';
import { useAppSelector } from '../../core/utils';
import useIncentiveOffers from '../PaymentCalculator/ViewOffers/useIncentiveOffers';
import usePriceFlagsForProvince from '../../utils/hooks/usePriceFlagsForProvince';
var parseQueryStringWithComma = function (queryString) { return (queryString ? queryString.split(',') : []); };
var ReducersProvider = function () {
    var _a, _b;
    var dispatch = useDispatch();
    var appName = useAppName();
    var search = useLocation().search;
    var canUseLoyalty = useAppSelector(selectCanUseLoyalty);
    var showSellingPrice = usePriceFlagsForProvince().showSellingPrice;
    var decodedQueryString = decodeURIComponent(search);
    var query = useMemo(function () { return new URLSearchParams(decodedQueryString); }, [decodedQueryString]);
    var defaultPaymentOptions = getDefaultPaymentOptionsConfig(appName);
    var defaultPaymentMethodConfig = getDefaultPaymentMethodConfig(appName);
    var _c = useIncentiveOffers(primaryCalculatorId), transmissionKey = _c.currentVehicleTransmissionKey, paymentMethod = _c.paymentMethod;
    var offerKeysParamValue = query.get(OFFER_KEYS_URL_PARAM);
    var loyaltyOfferKeyParam = query.get(LOYALTY_OFFER_KEY);
    var canUseLoyaltyParam = query.get(LOYALTY_URL_PARAM) === 'true';
    var includeTaxes = query.get(INCLUDE_TAXES_URL_PARAM) === 'true';
    var includeFees = ['', 'true'].includes((_a = query.get(INCLUDE_FEES_URL_PARAM)) !== null && _a !== void 0 ? _a : '');
    var warrantyType = (_b = query.get(BapUrlParam.WARRANTY_TYPE)) !== null && _b !== void 0 ? _b : '';
    useEffect(function () {
        var _a;
        var parsedVehicleQuery = {
            modelKey: query.get('modelKey') || '',
            modelYear: query.get('modelYear') || '',
            transmissionKey: query.get('transmissionKey') || '',
            trimKey: query.get('trimKey') || '',
            exteriorColorKey: query.get('exteriorColorKey') || '',
            interiorColorKey: query.get('interiorColorKey') || '',
            accessoryKeys: parseQueryStringWithComma(query.get('accessoryKeys')),
            protectionKeys: parseQueryStringWithComma(query.get('protectionKeys')),
            protectionAddOnKeys: parseQueryStringWithComma(query.get('protectionAddOnKeys')),
            inventoryStatus: query.get('inventoryStatus') || '',
        };
        var warrantyKey = query.get(CalculatorUrlKeys.warrantyKey) || '';
        var incentiveOfferIds = parseQueryStringWithComma(offerKeysParamValue).map(Number).filter(Boolean);
        var parsedCalculatorQuery = __assign(__assign((_a = { paymentMethod: PaymentMethod[query.get(CalculatorUrlKeys.paymentMethod)] ||
                    defaultPaymentOptions.paymentMethod, paymentFrequency: PaymentFrequency[query.get(CalculatorUrlKeys.paymentFrequency)] ||
                    defaultPaymentOptions.paymentFrequency, termAndRate: Number(query.get(CalculatorUrlKeys.termAndRate)) || defaultPaymentOptions.termAndRate }, _a[CalculatorFields.DownPaymentAmount] = Number(query.get(CalculatorUrlKeys.downPayment)) || 0, _a[CalculatorFields.TradeInValueAmount] = Number(query.get(CalculatorUrlKeys.tradeInValue)) || 0, _a[CalculatorFields.TradeInOwingAmount] = Number(query.get(CalculatorUrlKeys.tradeInOwing)) || 0, _a.warrantyKey = warrantyKey, _a.incentiveOfferIds = incentiveOfferIds, _a), (!warrantyKey && { warrantyAmount: 0 })), { leaseAnnualKmAllowance: Number(query.get('leaseAnnualKmAllowance')) || 0, additionalLeaseAnnualKmAllowance: Number(query.get('leaseAdditionalAnnualKm')) || 0, overriddenIsSellingProvince: includeFees, includeFees: includeTaxes });
        dispatch(brandNameDetected(appName));
        dispatch(defaultPaymentOptionsDetected(defaultPaymentOptions));
        dispatch(defaultPaymentMethodOptionsDetected(defaultPaymentMethodConfig));
        dispatch(updateCurrentVehicleFromUrl(parsedVehicleQuery));
        dispatch(updatePrimaryCalculatorFromUrl(parsedCalculatorQuery));
        if (warrantyType)
            dispatch(setWarrantyOptionsModalType(warrantyType));
    }, [
        appName,
        defaultPaymentOptions.paymentFrequency,
        defaultPaymentOptions.paymentMethod,
        defaultPaymentOptions.termAndRate,
        query,
    ]);
    useEffect(function () {
        if (!query.has(OFFER_KEYS_URL_PARAM))
            return;
        dispatch(canSkipDefaultNonStackableOffersChanged(true));
        /* using "query" as a dependency in this useEffect requires "updateCondition: () => true" for
          "queryParam: OFFER_KEYS_URL_PARAM" in src/components/ConfigureBap/hooks/useSyncBAPStateToURL.ts.
          This effect runs everytime "query" changes, rather than only on mount, due to changes we want to
          track after internal redirects. As such, the OFFER_KEYS_URL_PARAM url param is made to persist for
          every state change that happens after this component mounts, making this callback return early
          for all future runs of this useEffect.
        */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query]);
    var isOffersInitialized = useRef(false);
    useEffect(function () {
        var canSkipOffersUpdate = !transmissionKey || !paymentMethod || isOffersInitialized.current;
        if (canSkipOffersUpdate)
            return;
        dispatch(offersPrimaryChanged({
            calculatorId: primaryCalculatorId,
            transmissionKey: transmissionKey,
            paymentMethod: paymentMethod,
            offerIds: parseQueryStringWithComma(offerKeysParamValue).map(Number).filter(Boolean),
            canCreateIfEmpty: true,
        }));
        isOffersInitialized.current = true;
    }, [transmissionKey, paymentMethod, offerKeysParamValue]);
    // TODO: This feels like it should be somewhere else. It re-renders this component an extra time,
    //        the first with the default value, then again when it detects the user's actual province.
    //        I separated it from the other useEffect because it's not related to URL query params and
    //        there is no need to execute that code extra times.
    useEffect(function () {
        dispatch(setPrimaryCalculatorOverriddenIsSellingProvince(showSellingPrice));
    }, [dispatch, showSellingPrice]);
    useEffect(function () {
        if (!canUseLoyaltyParam)
            return;
        dispatch(canUseLoyaltyChanged(canUseLoyaltyParam));
        dispatch(setIsLoyaltyFilterChecked(canUseLoyaltyParam));
    }, [canUseLoyaltyParam]);
    useEffect(function () {
        if (!canUseLoyalty || loyaltyOfferKeyParam === null)
            return;
        dispatch(loyaltyOfferChanged({ calculatorId: primaryCalculatorId, offerKey: loyaltyOfferKeyParam }));
    }, [loyaltyOfferKeyParam, canUseLoyalty]);
    useEffect(function () {
        var canUpdateLoyalty = canUseLoyalty && loyaltyOfferKeyParam !== null && transmissionKey && paymentMethod;
        if (!canUpdateLoyalty)
            return;
        dispatch(loyaltyPrimaryChanged({
            calculatorId: primaryCalculatorId,
            offerKey: loyaltyOfferKeyParam,
            transmissionKey: transmissionKey,
            paymentMethod: paymentMethod,
        }));
    }, [loyaltyOfferKeyParam, transmissionKey, paymentMethod, canUseLoyalty]);
    return null;
};
export default ReducersProvider;
